import { useEffect, useState } from 'react';
import { CarFront, Send, MessageSquare, Bug, RefreshCw, ChevronUp, Share2 } from 'lucide-react';
import { ShareDialog } from '@/components/share/ShareDialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useCompare } from '@/contexts/CompareContext';

type ActiveDialog = null | 'suggest' | 'feedback';

export function FloatingActions() {
  const [activeDialog, setActiveDialog] = useState<ActiveDialog>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const navigate = useNavigate();
  const { selectedCars } = useCompare();
  const hasCompareBar = selectedCars.length > 0;

  const handleShare = async () => {
    setMenuOpen(false);
    const title = typeof document !== 'undefined' ? document.title : 'Min Nye Elbil';
    const url = typeof window !== 'undefined' ? window.location.href : '';
    if (typeof navigator !== 'undefined' && 'share' in navigator && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
      }
    }
    setShareOpen(true);
  };

  // Mobil: knapperne bor i bundmenuens "Mere" og styres via events
  useEffect(() => {
    const openFeedback = () => setActiveDialog('feedback');
    const openSuggest = () => setActiveDialog('suggest');
    const share = () => { void handleShare(); };
    window.addEventListener('mne:open-feedback', openFeedback);
    window.addEventListener('mne:open-suggest', openSuggest);
    window.addEventListener('mne:share-page', share);
    return () => {
      window.removeEventListener('mne:open-feedback', openFeedback);
      window.removeEventListener('mne:open-suggest', openSuggest);
      window.removeEventListener('mne:share-page', share);
    };
  }, []);

  return (
    <>
      {/* Single floating button with expandable menu */}
      <div className={`hidden lg:flex fixed right-3 sm:right-4 z-40 flex-col gap-2 items-end transition-all ${hasCompareBar ? 'bottom-20 sm:bottom-16' : 'bottom-4'}`}>

        {menuOpen && (
          <div className="flex flex-col gap-2 animate-fade-in">
            <Button
              onClick={handleShare}
              variant="outline"
              size="sm"
              className="gap-2 shadow-lg bg-card hover:bg-secondary"
            >
              <Share2 className="w-4 h-4" />
              Del denne side
            </Button>
            <Button
              onClick={() => { navigate('/bidrag'); setMenuOpen(false); }}
              variant="outline"
              size="sm"
              className="gap-2 shadow-lg bg-card hover:bg-secondary"
            >
              <RefreshCw className="w-4 h-4" />
              Opdater elbildata
            </Button>
            <Button
              onClick={() => { setActiveDialog('suggest'); setMenuOpen(false); }}
              variant="outline"
              size="sm"
              className="gap-2 shadow-lg bg-card hover:bg-secondary"
            >
              <CarFront className="w-4 h-4" />
              Mangler vi en elbil?
            </Button>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Button
            onClick={handleShare}
            size="icon"
            variant="outline"
            aria-label="Del denne side"
            className="shadow-lg bg-card hover:bg-secondary rounded-full"
          >
            <Share2 className="w-4 h-4" />
          </Button>
          <Button
            onClick={() => menuOpen ? setMenuOpen(false) : setActiveDialog('feedback')}
            onContextMenu={(e) => { e.preventDefault(); setMenuOpen(!menuOpen); }}
            className="gap-2 shadow-lg bg-accent text-accent-foreground hover:bg-accent/90"
          >
            {menuOpen ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <MessageSquare className="w-4 h-4" />
            )}
            {menuOpen ? 'Luk' : 'Feedback'}
          </Button>
        </div>
        {!menuOpen && (
          <button
            onClick={() => setMenuOpen(true)}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Mere ▲
          </button>
        )}

      </div>

      <SuggestCarDialog
        open={activeDialog === 'suggest'}
        onOpenChange={(open) => setActiveDialog(open ? 'suggest' : null)}
      />
      <FeedbackDialog
        open={activeDialog === 'feedback'}
        onOpenChange={(open) => setActiveDialog(open ? 'feedback' : null)}
      />
      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        title={typeof document !== 'undefined' ? document.title : 'Min Nye Elbil'}
        url={typeof window !== 'undefined' ? window.location.href : ''}
        context="generic"
      />
    </>
  );
}

function SuggestCarDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [variant, setVariant] = useState('');
  const [link, setLink] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!brand.trim() || !model.trim()) {
      toast.error('Angiv venligst mærke og model');
      return;
    }
    setIsSubmitting(true);
    try {
      const message = [
        `Mærke: ${brand.trim()}`,
        `Model: ${model.trim()}`,
        variant.trim() && `Variant: ${variant.trim()}`,
        link.trim() && `Link: ${link.trim()}`,
      ].filter(Boolean).join('\n');

      const { error } = await supabase.from('feedback').insert({
        type: 'missing_car',
        message,
        email: email.trim() || null,
        page_url: window.location.href,
      });
      if (error) throw error;
      toast.success('Tak! Vi kigger på at tilføje elbilen.');
      setBrand(''); setModel(''); setVariant(''); setLink(''); setEmail('');
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting car suggestion:', error);
      toast.error('Der opstod en fejl. Prøv venligst igen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Foreslå en elbil vi mangler</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="suggest-brand">Mærke *</Label>
            <Input id="suggest-brand" placeholder="f.eks. Tesla, BMW, Volvo..." value={brand} onChange={(e) => setBrand(e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="suggest-model">Model *</Label>
            <Input id="suggest-model" placeholder="f.eks. Model 3, iX3, EX30..." value={model} onChange={(e) => setModel(e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="suggest-variant">Variant (valgfrit)</Label>
            <Input id="suggest-variant" placeholder="f.eks. Long Range, M Sport..." value={variant} onChange={(e) => setVariant(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="suggest-link">Link til elbilens hjemmeside (valgfrit)</Label>
            <Input id="suggest-link" type="url" placeholder="https://..." value={link} onChange={(e) => setLink(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="suggest-email">Din email (valgfrit)</Label>
            <Input id="suggest-email" type="email" placeholder="din@email.dk" value={email} onChange={(e) => setEmail(e.target.value)} />
            <p className="text-xs text-muted-foreground">Hvis du vil have besked når elbilen er tilføjet</p>
          </div>
          <Button type="submit" className="w-full gap-2" disabled={isSubmitting}>
            <Send className="w-4 h-4" />
            {isSubmitting ? 'Sender...' : 'Send forslag'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FeedbackDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const [type, setType] = useState<'feedback' | 'bug'>('feedback');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error('Skriv venligst en besked');
      return;
    }
    setIsSubmitting(true);
    try {
      const { error } = await supabase.from('feedback').insert({
        type,
        message: message.trim(),
        email: email.trim() || null,
        page_url: window.location.href,
      });
      if (error) throw error;
      toast.success(type === 'feedback' ? 'Tak for din feedback!' : 'Tak for din fejlrapport!');
      setMessage(''); setEmail(''); setType('feedback');
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Der opstod en fejl. Prøv venligst igen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Giv feedback eller rapportér fejl</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Type</Label>
            <RadioGroup value={type} onValueChange={(v) => setType(v as 'feedback' | 'bug')} className="flex gap-4">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="feedback" id="fb-feedback" />
                <Label htmlFor="fb-feedback" className="flex items-center gap-2 cursor-pointer">
                  <MessageSquare className="w-4 h-4" /> Feedback
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="bug" id="fb-bug" />
                <Label htmlFor="fb-bug" className="flex items-center gap-2 cursor-pointer">
                  <Bug className="w-4 h-4" /> Fejlrapport
                </Label>
              </div>
            </RadioGroup>
          </div>
          <div className="space-y-2">
            <Label htmlFor="fb-message">{type === 'feedback' ? 'Din feedback' : 'Beskriv fejlen'}</Label>
            <Textarea id="fb-message" placeholder={type === 'feedback' ? 'Fortæl os hvad du synes...' : 'Beskriv hvad der gik galt...'} value={message} onChange={(e) => setMessage(e.target.value)} rows={4} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="fb-email">Email (valgfrit)</Label>
            <Input id="fb-email" type="email" placeholder="din@email.dk" value={email} onChange={(e) => setEmail(e.target.value)} />
            <p className="text-xs text-muted-foreground">Hvis du vil have svar, så angiv din email</p>
          </div>
          <Button type="submit" className="w-full gap-2" disabled={isSubmitting}>
            <Send className="w-4 h-4" />
            {isSubmitting ? 'Sender...' : 'Send'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
